<template>
    <div class="card">
        <div class="table-responsive" style="min-height: 400px;">
            <table class="table">
                <thead>
                    <tr>
                        <th>Invoice No</th>
                        <th>Invoice Date</th>
                        <th>Customer Name</th>
                        <th class="text-right">Invoice Amount</th>
                        <th class="text-right">Settlement Discount</th>
                        <th class="text-right">AIT Deduction</th>
                        <th class="text-right">VAT Deduction</th>
                        <th class="text-right">Paid Amount</th>
                        <th class="text-right">Due Amount</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, i) in purchase" :key="i">
                        <td> {{ item.bill_number }}</td>
                        <td> {{ item.date }}</td>
                        <td> {{ item.party_name }}</td>
                        <td class="text-right"> {{ commaFormat(item.bill_amount) }}</td>
                        <td class="text-right"> {{ commaFormat(item.total_discount) }} </td>
                        <td class="text-right"> {{ commaFormat(item.ait_deducted) }}</td>
                        <td class="text-right"> {{ commaFormat(item.vat_deducted) }}</td>
                        <td class="text-right"> {{ commaFormat(item.paid_amount)}}</td>
                        <td class="text-right"> {{ commaFormat(getTotalDue(item)) }}</td>
                        <td>
                            <div class="d-flex">
                                <button class="btn btn-primary btn-sm mx-2"
                                    @click="goToEdit(item.id)"
                                    style="cursor: pointer"
                                >
                                    <i class="fas fa-eye"></i>
                                </button>
                                <div class="dropdown">
                                  <button class="btn btn-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown">
                                      <i class="fas fa-ellipsis-v"></i>
                                  </button>
                                  <ul class="dropdown-menu">
                                      <li>
                                          <a class="dropdown-item" href="#" @click.prevent="goToPrint(item.id, 1)">
                                              <i class="fas fa-print me-2"></i> Print Invoice
                                          </a>
                                      </li>
                                      <li>
                                          <a class="dropdown-item" href="#" @click.prevent="goToPrint(item.id, 2)">
                                              <i class="fas fa-print me-2"></i> Print Challan
                                          </a>
                                      </li>
                                      <li>
                                          <a class="dropdown-item" href="#" @click.prevent="goToPrint(item.id, 3)">
                                              <i class="fas fa-print me-2"></i> Print Mushak 6.3
                                          </a>
                                      </li>
                                  </ul>
                                </div>
                            </div>

                        </td>
                    </tr>
                </tbody>
            </table>
            <p v-if="!purchase.length" class="text-center mt-5">No data</p>
        </div>
    </div>
</template>

<style scoped>
.btn-challan {
    background-color: #249024 !important;
}
</style> 

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import figureFormatter from '@/services/utils/figureFormatter';
import handlePurchase from '@/services/modules/purchase';
import pdfPrinter from '@/services/utils/pdfPrinter';
import handleCompany from '@/services/modules/company';
import handleBarcode from '@/services/modules/barcode';
import handleHospital from '@/services/modules/hospital';
import useDate from '@/services/utils/day'
import printMushak63 from '@/services/utils/pdf/mushakPdf'

const props = defineProps({
  purchase: Array,
  route_name: {
    type: String,
    default: 'purchase-bill-edit'
  }
});

const { commaFormat } = figureFormatter();
const { fetchSalesBill } = handlePurchase();
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
const { exportToPDF } = pdfPrinter();
const { fetchBarcode } = handleBarcode();
const { notesByType } = handleHospital();
const { formatDate } = useDate();
const { exportToPdfMushak } = printMushak63();

const route = useRoute();
const router = useRouter();
const store = useStore();

const companyInfo = ref({});
const invoice = ref({});
const barcodeQuery = ref('');
const barcode = ref('');
const qrcode = ref('');
const customizationPdfFields = ref([]);
const invoiceNotes = ref([]);
const user = computed(() => store.state.user.user);

onMounted(async () => {
  try {
    const res = await fetchCompanyInfo(route.params.companyId);
    companyInfo.value = res.data;
  } catch (error) {
    console.log(error);
  }

  await getInvoiceNotes();
  await getActiveFiledCustomization();
});

const getInvoiceNotes = async () => {
  const companyQuery = `?company_id=${route.params.companyId}&show_default_only=1`;
  const res = await notesByType(companyQuery, 'invoice_terms');
  if (res.status) {
    invoiceNotes.value = res.data.filter((item) => item.full_desc);
  }
};

const getActiveFiledCustomization = async () => {
  const companyQuery = `?company_id=${route.params.companyId}&customization_type=pdf&type=sale_invoice_pdf_list`;
  const res = await fetchActiveCustomizationField(companyQuery);
  customizationPdfFields.value = res.data;
};

const goToEdit = (id) => {
  router.push({
    name: 'sales-invoice-template-edit',
    params: {
      companyId: route.params.companyId,
      saleId: id,
    },
    query:route.query
  });
};

const goToPrint = async (id, btnFunc) => {
  try {
    const companyQuery = `?company_id=${route.params.companyId}`;
    const res = await fetchSalesBill(id, companyQuery);

    if (res) {
      invoice.value = res.data;
      const qrcodeData = JSON.stringify({
        ref_no: invoice.value.bill_number,
        ref_date: invoice.value.date,
        created_by: invoice.value.created_by_name,
        created_date_time: formatDate(new Date())
      });

      barcodeQuery.value = `?code=${invoice.value.bill_number}&qrcode=${qrcodeData}`;

      const barcodeRes = await fetchBarcode(barcodeQuery.value);
      barcode.value = barcodeRes.barcode;
      qrcode.value = barcodeRes.qrcode;

      if(btnFunc === 3) {
        exportToPdfMushak(
          companyInfo.value,
          invoice.value,
          user.value,
        );
        return;
      }

      exportToPDF(
        companyInfo.value,
        invoice.value,
        barcode.value,
        qrcode.value,
        user.value.name,
        btnFunc,
        invoiceNotes.value,
        false,
        customizationPdfFields.value
      );
    }
  } catch (err) {
    console.log(err);
  }
};

const getTotalDue = (item) => {
    return parseFloat(item.bill_amount) - parseFloat(item.net_receipt_amount) - parseFloat(item.ait_deducted) - parseFloat(item.vat_deducted) - parseFloat(item.discount)
}
</script>